import { useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import { getCollatedUpdates } from "../../utils/requests/collatedUpdatesRequest";
import { CollatedEntry, CollatedUpdatesResponse } from "../../types/collatedUpdates";

// The only scopes this hook is ever called with for a given item. Shared with
// `useCreateUpdate` (the optimistic write hook), which needs to mutate the
// exact same `useSWRInfinite` cache entries this hook registers.
export const UPDATE_SCOPES = ["updates", "all"] as const;
export type UpdateScope = (typeof UPDATE_SCOPES)[number];

// Single source of truth for this hook's `useSWRInfinite` key shape. Anything
// that needs to reach this cache from outside the hook (e.g. `useCreateUpdate`
// doing an optimistic update) must build its key with this function — never
// hand-copy the tuple shape — so the two can't silently drift apart.
export const collatedUpdatesKey = (itemId: string, scope: UpdateScope, page = 1) =>
  [itemId, scope, page] as const; // [itemId, scope, page]

const useCollatedUpdates = (itemId: string, scope: UpdateScope = "updates") => {
  const getKey = (index: number, prev: CollatedUpdatesResponse | null) => {
    if (!itemId) return null; // don't fetch without an item
    if (prev && !prev.hasMore) return null; // reached the end
    return collatedUpdatesKey(itemId, scope, index + 1);
  };

  const { data, error, size, setSize, isLoading, isValidating, mutate } = useSWRInfinite(
    getKey,
    ([id, sc, page]) => getCollatedUpdates(id, page, sc),
    // `revalidateFirstPage` defaults to true, which would re-fetch page 1 on every
    // `setSize` bump. That refetch would clobber the already-loaded first page and,
    // combined with a stale `previousPageData`, can short-circuit pagination before
    // later pages are ever requested. Disable it — this is a load-more list, not a
    // "keep page 1 freshest" feed.
    { revalidateFirstPage: false },
  );

  // Depend on `data` (a stable SWR reference), not a freshly-allocated
  // `data ?? []` — otherwise the fallback `[]` is a new array every render and
  // this memo would recompute each time.
  const entries = useMemo(() => {
    const pages = (data ?? []) as CollatedUpdatesResponse[];
    const seen = new Set<string>();
    const out: CollatedEntry[] = [];
    for (const p of pages) {
      for (const e of p.entries) {
        const key = e.dedupKey ?? `${e.author?.id ?? "unknown"}::${(e as any).body ?? e.id}`;
        if (seen.has(key)) continue; // drop straddling duplicate seen on a prior page
        seen.add(key);
        out.push(e);
      }
    }
    return out;
  }, [data]);

  // Not a hook dependency, so a fresh `[]` fallback here is harmless.
  const pages = (data ?? []) as CollatedUpdatesResponse[];
  const hasMore = pages.length ? pages[pages.length - 1].hasMore : false;

  return {
    entries,
    hasMore,
    isLoadingInitial: isLoading,
    isLoadingMore: isValidating && size > 1,
    loadMore: () => setSize(size + 1),
    error,
    mutate,
  };
};

export default useCollatedUpdates;
